import { useEffect, lazy, Suspense } from 'react';
import { Routes, Route, useParams, useLocation } from 'react-router-dom';
import { Spinner } from '@/components/ui/spinner';
import { useSubdomain } from './hooks/useSubdomain';
import { useAuth } from './contexts/AuthContext';
import { useUIStore } from './stores/uiStore';
import { getProfileUrl, isLocalhost } from './utils/urls';

// Eagerly load main pages for best UX
import Feed from './pages/Feed';
import Profile from './pages/Profile';
import MainLayout from './components/layout/MainLayout';
import Navbar from './components/layout/Navbar';
import GlobalModals from './components/GlobalModals';

// Lazy load secondary pages for smaller initial bundle
const CommitDetail = lazy(() => import('./pages/CommitDetail'));
const TagPage = lazy(() => import('./pages/TagPage'));
const Login = lazy(() => import('./pages/Login'));
const AuthCallback = lazy(() => import('./pages/AuthCallback'));
const GitHubCallback = lazy(() => import('./pages/GitHubCallback'));
const OGTestCommit = lazy(() => import('./pages/OGTestCommit'));
const OGTestProfile = lazy(() => import('./pages/OGTestProfile'));
const InviteLanding = lazy(() => import('./pages/InviteLanding'));
const Terms = lazy(() => import('./pages/Terms'));
const Privacy = lazy(() => import('./pages/Privacy'));

// Loading fallback for lazy-loaded pages
function PageLoader() {
  return (
    <div className="min-h-screen flex items-center justify-center">
      <Spinner size="lg" />
    </div>
  );
}

// Redirects /:username path to username.commit.you subdomain (in production)
// In development (localhost), renders the Profile component directly
function ProfileRedirect() {
  const { username, branch } = useParams<{ username: string; branch?: string }>();

  useEffect(() => {
    if (username && !isLocalhost) {
      // In production, redirect to subdomain
      window.location.href = getProfileUrl(username, branch);
    }
  }, [username, branch]);

  // In development, render Profile directly
  if (isLocalhost) {
    return <Profile initialBranch={branch} />;
  }

  // Show loading while redirecting in production
  return (
    <div className="min-h-screen flex items-center justify-center">
      <Spinner size="lg" />
    </div>
  );
}

function App() {
  const subdomain = useSubdomain();
  const location = useLocation(); // Subscribe to URL changes
  const { isAuthenticated } = useAuth();
  const openCommitEditor = useUIStore((s) => s.openCommitEditor);

  // Check for invite subdomain (invite.commit.you)
  const hostname = typeof window !== 'undefined' ? window.location.hostname : '';
  const isInviteSubdomain = hostname === 'invite.commit.you' || hostname.startsWith('invite.');

  if (isInviteSubdomain) {
    return (
      <Suspense fallback={<PageLoader />}>
        <InviteLanding />
      </Suspense>
    );
  }

  // If on a user subdomain (e.g., thiago.commit.you), show that user's profile
  if (subdomain) {
    // Redirect auth-related paths to main domain
    const path = location.pathname;
    if (path === '/login' || path.startsWith('/auth/')) {
      window.location.href = `https://commit.you${path}${window.location.search}`;
      return null;
    }

    // Extract branch from path (e.g., /my-branch)
    const branchMatch = path.match(/^\/([^/]+)$/);
    const initialBranch = branchMatch ? branchMatch[1] : undefined;

    return (
      <div className="min-h-screen bg-[hsl(var(--background))]">
        <Navbar onNewCommit={isAuthenticated ? () => openCommitEditor() : undefined} />
        <Profile username={subdomain} initialBranch={initialBranch} />
        <GlobalModals />
      </div>
    );
  }

  // Normal routing for main domain (commit.you)
  return (
    <div className="min-h-screen bg-[hsl(var(--background))]">
      <Routes>
        {/* Auth routes - no shared layout, need their own Suspense */}
        <Route path="/login" element={<Suspense fallback={<PageLoader />}><Login /></Suspense>} />
        <Route path="/auth/callback" element={<Suspense fallback={<PageLoader />}><AuthCallback /></Suspense>} />
        <Route path="/auth/github/callback" element={<Suspense fallback={<PageLoader />}><GitHubCallback /></Suspense>} />

        {/* Legal pages */}
        <Route path="/terms" element={<Suspense fallback={<PageLoader />}><Terms /></Suspense>} />
        <Route path="/privacy" element={<Suspense fallback={<PageLoader />}><Privacy /></Suspense>} />

        {/* Dev/test routes - localhost only */}
        {isLocalhost && (
          <>
            <Route path="/ogtest/commit" element={<Suspense fallback={<PageLoader />}><OGTestCommit /></Suspense>} />
            <Route path="/ogtest/profile" element={<Suspense fallback={<PageLoader />}><OGTestProfile /></Suspense>} />
            <Route path="/invite" element={<Suspense fallback={<PageLoader />}><InviteLanding /></Suspense>} />
          </>
        )}

        {/* Main routes - shared layout with persistent Navbar (Suspense inside MainLayout) */}
        <Route element={<MainLayout />}>
          <Route path="/" element={<Feed />} />
          <Route path="/c/:id" element={<CommitDetail />} />
          <Route path="/tag/:tag" element={<TagPage />} />
          <Route path="/:username/:branch" element={<ProfileRedirect />} />
          <Route path="/:username" element={<ProfileRedirect />} />
        </Route>
      </Routes>
    </div>
  );
}

export default App;
